import React, { useEffect, useState } from 'react';
import { useResultList } from './hooks/useResultList';
import { Link } from 'react-router-dom';
import LoadingSpiner from '../Loader/LoadingSpiner';
import { FaExclamationCircle } from 'react-icons/fa';
import EmptyResult from './EmptyResult';
import { useDispatch, useSelector } from 'react-redux';
import { selectResultDirection, selectResultIds, selectResultIdsState, selectResultSortField, setSortField, setSortDirection } from '../../Store/resultSlice';
import { clearSelectedImageIds } from '../../Store/imageSlice';
import { obfuscateImageName } from '../../utils/sizeToText';

export interface Result {
    id: string;
    name: string;
    created_by: string;
    owner_id: string;
    status: string;
    progress: number;
    segmentation_type: string;
    model_path: string;
    created_at: string;
}

interface ResultListProps {
    imageId: string;
    thumbnail_url: string | null;
}

// Add new types for sorting
type SortField = keyof Result | '';

export default function ResultList(props: ResultListProps) {
    const { imageId, thumbnail_url } = props;
    const { data: results, isLoading, isError } = useResultList(imageId);
    const sortByField = useSelector(selectResultSortField);
    const sortDirection = useSelector(selectResultDirection);
    const selectedResultIds = useSelector(selectResultIdsState);
    const dispatch = useDispatch()

    // Add sort handler
    const handleSort = (field: SortField) => {
        if (sortByField === field) {
            dispatch(setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc'));
        } else {
            dispatch(setSortField(field));
            dispatch(setSortDirection('desc'));
        }
    };

    // Add sort function
    const sortResults = (results: Result[]) => {
        if (!sortByField) return results;

        return [...results].sort((a, b) => {
            const aValue = a[sortByField as keyof Result];
            const bValue = b[sortByField as keyof Result];

            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    };

    if (isLoading) return <div className='text-center mt-6'><LoadingSpiner/></div>;
    if (isError) return <div><p className='text-center mt-6'><FaExclamationCircle className='inline mr-2'/> Error loading results</p></div>;

    const handleResultSelect = (resultId: string) => {
        const currentIndex = selectedResultIds.indexOf(resultId);
        const newSelectedResults = Array.from(selectedResultIds);

        if (currentIndex === -1) {
            newSelectedResults.push(resultId);
        } else {
            newSelectedResults.splice(currentIndex, 1);
        }

        dispatch(selectResultIds(newSelectedResults));
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked && results) {
            const newSelectedResults = results.map((result: any) => result.id);
            dispatch(selectResultIds(newSelectedResults));
            return;
        }
        dispatch(selectResultIds([]))
    };

    const isAllSelected = results?.length === selectedResultIds.length;
    const emptyResults = results?.length === 0;

    if(emptyResults){
        return (
            <EmptyResult imageId={imageId} thumbnail_url={thumbnail_url}/>
        )
    }

    const isTMAImage = (results: any[]) => {
        // Find a segmentation result (not quality) and check if it has multiple result_data items
        const segmentationResult = results.find(result => 
            !result.model_path?.toLowerCase().includes('quality') &&
            !result.model_path?.toLowerCase().includes('processing-quality-classification')
        );
        return segmentationResult?.result_data?.length > 1;
    };

    const removedFailureResults = results.filter((result: any) => {
        // Always remove failed results
        if (result.status === "Failed") return false;
        
        // For TMA images, remove quality results
        if (isTMAImage(results)) {
            return !result.model_path?.toLowerCase().includes('quality') &&
                   !result.model_path?.toLowerCase().includes('processing-quality-classification');
        }
        
        // For non-TMA images, show all results
        return true;
    });

    if(!removedFailureResults.length){
        return (
            <div className="overflow-x-auto mt-6">
                <p>Please resubmit this image for analysis because there has been an error during the last submission.</p>
            </div>
        );
    }

    const getModelName = (modelPath: string) => {
        if (!modelPath) return '';
        
        const parts = modelPath.split('/');
        if (parts.length < 4) return '';

        const modelType = parts[2].replace(/-/g, ' ');
        const modelVersion = parts[3].split('_')[0].replace(/-/g, ' ');
        
        return `${modelType} - ${modelVersion}`;
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatResultName = (name: string) => {
        const obfuscatedName = obfuscateImageName(name);
        const type = name.toLowerCase().includes('quality') ? 'Quality' : 'Segmentation';
        return `${obfuscatedName} - ${type}`;
    };

    return (
        <div className="overflow-x-auto mt-6">
            <table className="table w-full">
                <thead>
                    <tr>
                        <th className="w-12">
                            <label>
                                <input
                                    type="checkbox"
                                    className="checkbox"
                                    onChange={handleSelectAllClick}
                                    checked={isAllSelected}
                                />
                            </label>
                        </th>
                        <th onClick={() => handleSort('name')} className="cursor-pointer hover:bg-base-200">
                            Result Name {sortByField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th onClick={() => handleSort('created_at')} className="cursor-pointer hover:bg-base-200">
                            Date {sortByField === 'created_at' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th onClick={() => handleSort('status')} className="cursor-pointer hover:bg-base-200">
                            Status {sortByField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th onClick={() => handleSort('segmentation_type')} className="cursor-pointer hover:bg-base-200">
                            Type {sortByField === 'segmentation_type' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        {/* <th onClick={() => handleSort('model_path')} className="cursor-pointer hover:bg-base-200">
                            Model {sortByField === 'model_path' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </th> */}
                        <th>Who can access</th>
                        {/* <th className="text-right"></th> */}
                    </tr>
                </thead>
                <tbody>
                    {sortResults(removedFailureResults)?.map((result: Result) => (
                        <tr key={result.id}>
                            <th className="w-12">
                                <label>
                                    <input
                                        type="checkbox"
                                        className="checkbox"
                                        onChange={() => handleResultSelect(result.id)}
                                        checked={selectedResultIds.includes(result.id)}
                                    />
                                </label>
                            </th>
                            <td>
                                <div className="flex items-center space-x-3">
                                    <div><Link to={`results/${result.id}`}>{formatResultName(result.name)}</Link></div>
                                </div>
                            </td>
                            <td>
                                <div className="flex items-center space-x-3">
                                    <div>{formatDate(result.created_at)}</div>
                                </div>
                            </td>
                            <td>
                                <div className="flex flex-col gap-1 min-w-[9rem]">
                                    <div>{result.status}</div>
                                    {result.progress !== undefined && result.progress !== null && (
                                        <div className="flex items-center gap-2">
                                            {result.status === 'Synthesizing...' ? (
                                                // No value = daisyUI's indeterminate/animated bar - the
                                                // tile count has already hit 100% at this point, but the
                                                // combine step has no sub-progress of its own to report.
                                                <progress className="progress progress-primary w-24"></progress>
                                            ) : (
                                                <>
                                                    <progress
                                                        className={`progress w-24 ${result.status === 'Completed' ? 'progress-success' : 'progress-primary'}`}
                                                        value={result.progress}
                                                        max={100}
                                                    ></progress>
                                                    <span className="text-xs text-gray-500 tabular-nums">
                                                        {result.progress.toFixed(2)}%
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </td>
                            <td>
                                <div className="flex items-center space-x-3">
                                    <div>{result?.segmentation_type || ""}</div>
                                </div>
                            </td>
                            {/* <td>
                                <div className="flex items-center space-x-3">
                                    <div>{getModelName(result.model_path)}</div>
                                </div>
                            </td> */}
                            <td>
                                Only you can
                            </td>
                            {/* <td className="text-right">
                                <button className="btn btn-ghost btn-xs">details</button>
                            </td> */}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
